<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" icon @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Fermer</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" icon @click="save">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                <span>Sauvegarder</span>
              </v-tooltip>
            </div>
          </v-toolbar>

          <v-card-text>
            <v-container fluid>
              <v-row density="compact">
                <v-col md="4" cols="12">
                  <v-select
                    :items="sparepartTypes"
                    label="Machine"
                    v-model="sparepart.sparepartType"
                  />
                </v-col>

                <v-col md="4" cols="12">
                  <v-select
                    :items="sparepartMakes"
                    label="Fabricant"
                    v-model="sparepart.sparepartMake"
                  />
                </v-col>

                <v-col md="4" cols="12">
                  <v-select
                    :items="sparepartStatuses"
                    label="Status"
                    v-model="sparepart.sparepartStatus"
                  />
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field v-model="sparepart.name">
                    <template #label>
                      Désignation JLA
                      <span class="text-red"><strong> *</strong></span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field
                    v-model="sparepart.reference"
                    hint="La référence de la pièce"
                  >
                    <template #label>
                      Référence
                      <span class="text-red"><strong> *</strong></span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field v-model="sparepart.frenchName">
                    <template #label>
                      Désignation française
                      <span class="text-red"><strong> *</strong></span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field
                    v-model="sparepart.price"
                    label="Prix"
                    prefix="€"
                    hint="Le prix de la pièce"
                  />
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field
                    v-model="sparepart.unitInStock"
                    hint="Le nombre des pièces en stock"
                  >
                    <template #label>
                      Solde
                      <span class="text-red"><strong> *</strong></span>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col md="4" cols="12">
                  <v-text-field
                    v-model="sparepart.minUnitInStock"
                    label="Solde minimum"
                    hint="La quantité minimum"
                  />
                </v-col>

                <v-col cols="12">
                  <v-switch
                    v-model="sparepart.notificationEnabled"
                    label="Activer les notifications"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/store/app";

import { ProductMake, ProductType, SparepartStatus } from "@/types";
import { SparePart } from "@/types";
import { getSparepartById, postSparepart, putSparepart } from "@/utils/sparepart-api";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const loading = ref(false);
const title = ref("");
const sparepart = ref<SparePart>({} as SparePart);

const sparepartTypes = computed(() => Object.values(ProductType));
const sparepartStatuses = computed(() => Object.values(SparepartStatus));
const sparepartMakes = computed(() => Object.values(ProductMake));

function isCreatingNewSparepart(): boolean {
  return route.name === "newsparepart" || route.params.id === "NewSparepart";
}

function initEmptySparepart() {
  sparepart.value = {
    notificationEnabled: true,
  } as SparePart;
}

async function save() {
  try {
    loading.value = true;
    const payload = { ...sparepart.value };

    if (!payload.id) {
      sparepart.value = await postSparepart(payload);
      appStore.sendSuccessNotice("New Sparepart has been added.");
      router.replace({
        name: "sparepart",
        params: { id: sparepart.value.id },
      });
      title.value = "Editer la pièce";
    } else {
      await putSparepart(payload.id, payload);
      appStore.sendSuccessNotice("Sparepart has been updated.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function cancel() {
  router.push({ name: "spareparts" });
}

onMounted(async () => {
  try {
    loading.value = true;

    if (isCreatingNewSparepart()) {
      title.value = "Nouvelle pièce";
      initEmptySparepart();
      return;
    }

    title.value = "Editer la pièce";
    sparepart.value = await getSparepartById(route.params.id as string);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
});
</script>
